import { useState } from 'react';
import { Shell } from '../Shell';
import { useNav } from '../nav';
import { useAsync } from '../../core/useAsync';
import { cachedLeague, getCurrentRound, getMe, getSeasonRounds } from '../../core/api';
import { gradFor, ordinal, paceLabel, closesIn } from '../format';
import { WelcomeCarousel } from '../WelcomeCarousel';
import type { CurrentRound, LeagueInfo, Me, MediaType, SeasonRound } from '../../core/types';
import s from './SeasonHome.module.css';

// Show the one-time welcome the first time someone opens a given league. `show` is
// computed each render (not a one-shot initializer) so it flips on once the league
// id has loaded, then stays off after dismiss / on later visits.
function useWelcome(leagueId: number | null) {
  const [dismissed, setDismissed] = useState(false);
  const key = leagueId != null ? `tpiap:welcomed:${leagueId}` : null;
  let seen = true;
  try { seen = !key || localStorage.getItem(key) === '1'; } catch { /* ignore */ }
  const dismiss = () => { try { if (key) localStorage.setItem(key, '1'); } catch { /* ignore */ } setDismissed(true); };
  return { show: !!key && !seen && !dismissed, dismiss };
}

const Gear = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>;
const People = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>;

// The season "playlist": the round that's live now as a featured card, then every
// round of the season with its state. Wired to the live API.

type State = 'done' | 'now' | 'next' | 'later';
const TAG: Record<State, string> = { done: 'Done', now: 'Now', next: 'Next', later: 'Later' };

function stateOf(r: SeasonRound, currentId: number | null, nextId: number | null): State {
  if (r.phase === 'CLOSED' || r.closed_at) return 'done';
  if (currentId != null && r.round_id === currentId) return 'now';
  if (r.round_id === nextId) return 'next';
  return 'later';
}

export function SeasonHome() {
  const nav = useNav();
  const me = useAsync<Me>(getMe, []);
  const league = useAsync<LeagueInfo>(cachedLeague, []);
  const seasonId = league.data?.season_id ?? null;
  const round = useAsync<CurrentRound | null>(getCurrentRound, []);
  const list = useAsync<SeasonRound[]>(() => (seasonId != null ? getSeasonRounds(seasonId) : Promise.resolve([])), [seasonId]);
  const isOwner = me.data?.user?.is_admin ?? false;
  const isJukebox = league.data?.league_type === 'jukebox';
  const welcome = useWelcome(league.data?.league_id ?? null);

  if (round.loading || league.loading || list.loading) {
    return <Shell><div className={s.load}>Loading…</div></Shell>;
  }

  const rounds = [...(list.data ?? [])].sort((a, b) => a.sequence_no - b.sequence_no);
  const current = round.data;
  const currentId = current?.round_id ?? null;
  const nextId = rounds.find((r) => r.phase === 'SCHEDULED' && r.round_id !== currentId)?.round_id ?? null;
  const currentRow = current ? rounds.find((r) => r.round_id === current.round_id) : undefined;
  const media: MediaType = currentRow?.media_type ?? 'audio';
  const voting = current?.phase === 'VOTING';

  return (
    <Shell>
      {welcome.show && league.data ? <WelcomeCarousel name={league.data.name} jukebox={isJukebox} onClose={welcome.dismiss} /> : null}
      <div className={s.appbar}>
        <span className={s.ctx}>{league.data?.name ?? 'Season'} · {rounds.length} round{rounds.length === 1 ? '' : 's'}</span>
        <span className={s.tools}>
          {isJukebox ? <button className={s.manage} onClick={() => nav?.go('propose')}>+ Propose</button> : null}
          <button className={s.manage} onClick={() => nav?.go('members')} aria-label="Members"><People />Members</button>
          {isOwner ? <button className={s.manage} onClick={() => nav?.go('admin')}><Gear />Manage</button> : null}
        </span>
      </div>

      {current ? (
        <div className={s.feat}>
          <div className={s.banner} style={{ ['--m' as string]: `var(--${media})`, background: 'linear-gradient(150deg, var(--m), color-mix(in srgb, var(--m) 35%, #000))' }}>
            <span className={s.badge}>{media} round</span>
            <span className={s.livepill}><span className={s.blink} />{voting ? 'Voting open' : 'Submissions open'}</span>
          </div>
          <div className={s.fbody}>
            <h2>{current.theme}</h2>
            {currentRow?.theme_note ? <p>{currentRow.theme_note}</p> : null}
            {(() => {
              const meta = [paceLabel(current.pace), closesIn(voting ? current.voting_deadline : current.submission_deadline)].filter(Boolean).join(' · ');
              return meta ? <div className={s.fmeta}>{meta}</div> : null;
            })()}
            <div className={s.fcta}>
              <button onClick={() => nav?.go(voting ? 'vote' : 'submit')}>{voting ? 'Vote now →' : 'Submit →'}</button>
              <small>{voting ? `${current.ballots_in} of ${current.expected_players} voted` : `${current.submissions_in} of ${current.expected_players} in`}</small>
            </div>
          </div>
        </div>
      ) : null}

      <div className={s.sec}>This season</div>
      {rounds.length === 0 ? (
        isJukebox ? (
          <div className={s.empty}>
            <h3>No rounds yet — propose one to kick things off</h3>
            Everyone proposes a theme; they’re drawn into a running order, then each round you submit a track, vote, and guess who’s who.
            <button className={s.emptycta} onClick={() => nav?.go('propose')}>+ Propose a round</button>
          </div>
        ) : isOwner ? (
          <div className={s.empty}>
            <h3>No rounds yet — create the first one</h3>
            You build each round. Set a theme and open it when you’re ready — players then submit, vote, and guess.
            <button className={s.emptycta} onClick={() => nav?.go('admin')}>Go to admin →</button>
          </div>
        ) : (
          <div className={s.empty}><h3>Nothing scheduled yet</h3>The organiser hasn’t opened a round yet — it’ll appear here when they do.</div>
        )
      ) : (
        <div className={s.rounds}>
          {rounds.map((r) => {
            const st = stateOf(r, currentId, nextId);
            const meta = r.winner
              ? `${r.media_type} · ${r.your_placing ? `you came ${ordinal(r.your_placing)}` : 'done'}`
              : `${r.media_type} · ${st === 'now' ? (r.phase === 'VOTING' ? 'voting open' : 'submissions open') : st === 'next' ? 'opens next' : 'scheduled'}`;
            return (
              <button key={r.round_id} className={`${s.rround}${st === 'done' ? ' ' + s.done : ''}`} onClick={() => nav?.go(st === 'done' ? 'results' : st === 'now' ? (r.phase === 'VOTING' ? 'vote' : 'submit') : 'home')}>
                <span className={`${s.seq}${st === 'now' ? ' ' + s.now : ''}`}>{st === 'now' ? '♪' : String(r.sequence_no).padStart(2, '0')}</span>
                <span className={s.thumb} style={{ background: gradFor(r.round_id) }}><span className={s.pin} style={{ background: `var(--${r.media_type})` }} /></span>
                <span className={s.rmeta}>
                  <b>{r.theme}</b>
                  <small>{r.winner ? <span className={s.win}>Won by {r.winner.display_name}</span> : null}{r.winner ? ' · ' : ''}{meta}</small>
                </span>
                <span className={`${s.tag} ${s[st]}`}>{TAG[st]}</span>
              </button>
            );
          })}
        </div>
      )}
    </Shell>
  );
}
